import { useState, useEffect, useRef } from 'react';
import { Info, AlertCircle, ExternalLink } from 'lucide-react';
import FloatingInput from '../FloatingInput.jsx'; // Assuming this component exists
import SearchableSelect from '../SearchableSelect.jsx';
import { states, districts, getDistrictsForState } from '../../../data/locations.js';
import toast from 'react-hot-toast';

export function StatutoryDetailsForm({
    employeeData,
    setEmployeeData,
    onSaveSection,
    loading,
    feedback,
    darkMode,
    onCancel,
    onNext,
    isLastTab,
    isEmployeeView = false,
    readOnly = false,
    onSaveTrigger
}) {
    // State to manage the form data. This is the source of truth for the form inputs.
    const [formData, setFormData] = useState({
        hasUAN: null, // No default selection
        pfUANNumber: employeeData?.pfUANNumber || '',
        pfMemberId: employeeData?.pfMemberId || '',
        epsEligiblePreviousCompany: employeeData?.epsEligiblePreviousCompany ?? null,
        hasESIIP: employeeData?.hasESIIP ?? null,
        esiIPNumber: employeeData?.esiIPNumber || '',
        esiState: employeeData?.esiState || '',
        esiDistrict: employeeData?.esiDistrict || '',
        esiDispensary: employeeData?.esiDispensary || ''
    });

    // State to manage validation errors.
    const [formErrors, setFormErrors] = useState({});
    
    // State to manage tooltip visibility.
    const [showEPSTooltip, setShowEPSTooltip] = useState(false);
    
    // State to manage EPS confirmation modal
    const [showEPSModal, setShowEPSModal] = useState(false);
    const [pendingEPSSelection, setPendingEPSSelection] = useState(null);
    const isPfApplicable = employeeData?.pfApplicable === true;
    const isEsiApplicable = employeeData?.esiApplicable === true;
    const isOnlyEsiApplicable = isEsiApplicable && !isPfApplicable;

    // Track last synced employee ID so we only sync when loading a *different* employee.
    // This prevents overwriting the user's edits when employeeData gets updated from
    // other sources (e.g. other tab saves, parent re-renders) during edit.
    const lastSyncedEmployeeIdRef = useRef(null);

    // Ref to always call the latest save handler when parent (e.g. footer Update) triggers save.
    // Without this, the callback passed to onSaveTrigger would close over an old handleSaveOrNext
    // and old formData, causing "error on first save" and wrong data being sent.
    const saveHandlerRef = useRef(null);

    // Sync formData from employeeData only when we first load or switch to a different employee.
    // Do NOT sync on every employeeData change—that was overwriting user edits during save.
    useEffect(() => {
        if (!employeeData) return;
        const currentId = employeeData.id ?? 'new';
        if (lastSyncedEmployeeIdRef.current === currentId) return;
        lastSyncedEmployeeIdRef.current = currentId;
        setFormData({
            hasUAN: employeeData.hasUAN !== undefined ? Boolean(employeeData.hasUAN) : null,
            pfUANNumber: employeeData.pfUANNumber || '',
            pfMemberId: employeeData.pfMemberId || '',
            epsEligiblePreviousCompany: employeeData.epsEligiblePreviousCompany !== undefined ? Boolean(employeeData.epsEligiblePreviousCompany) : null,
            hasESIIP: employeeData.hasESIIP !== undefined ? Boolean(employeeData.hasESIIP) : null,
            esiIPNumber: employeeData.esiIPNumber || '',
            esiState: employeeData.esiState || '',
            esiDistrict: employeeData.esiDistrict || '',
            esiDispensary: employeeData.esiDispensary || ''
        });
    }, [employeeData?.id]);

    // Handles changes for all form inputs.
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let newValue;

        if (type === 'checkbox') {
            newValue = checked;
        } else {
            newValue = value;
        }
        
        // Special handling for radio buttons
        if (type === 'radio') {
            if (name === 'epsEligiblePreviousCompany') {
                // Show confirmation modal for EPS selection
                setPendingEPSSelection(value === "true");
                setShowEPSModal(true);
                return; // Don't update form data yet
            } else {
                newValue = value === "true";
            }
        }
        
        // Update the form data state.
        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        // Clear related fields when a 'No' radio option is selected.
        if (name === 'hasUAN' && !newValue) {
            setFormData(prev => ({ ...prev, pfUANNumber: '' }));
        }
        if (name === 'hasESIIP' && !newValue) {
            setFormData(prev => ({ ...prev, esiIPNumber: '' }));
        }
        
        // Clear district when state changes
        if (name === 'esiState') {
            setFormData(prev => ({ ...prev, esiDistrict: '' }));
        }
    };

    // A single, comprehensive validation function called on form submission.
    // It returns an errors object with all validation failures.
    const validateForm = () => {
        const errors = {};
        
        // Check UAN selection - This is mandatory only if PF is applicable
        if (!isOnlyEsiApplicable) {
            if (formData.hasUAN === null) {
                errors.hasUAN = 'Please select whether you have a UAN number.';
            } else if (formData.hasUAN === false) {
                // If user selects "No" for UAN, they cannot proceed
                errors.hasUAN = 'UAN number is mandatory to proceed. You must have a UAN number.';
            } else if (formData.hasUAN === true) {
                // Validate PF UAN Number if UAN is available
                if (!formData.pfUANNumber) {
                    errors.pfUANNumber = 'PF UAN Number is required.';
                } else if (formData.pfUANNumber.length !== 12) {
                    errors.pfUANNumber = 'PF UAN Number must be exactly 12 digits.';
                } else if (!/^\d{12}$/.test(formData.pfUANNumber)) {
                    errors.pfUANNumber = 'PF UAN Number must contain only digits.';
                }
            }
            
            // Check EPS eligibility selection - only if PF is applicable
            if (formData.epsEligiblePreviousCompany === null) {
                errors.epsEligiblePreviousCompany = 'Please select whether you are eligible for EPS deduction in your previous company.';
            }
        }

        // Optional field: if entered, PF Member ID must be exactly 22 alphanumeric chars.
        if (String(formData.pfMemberId || "").trim()) {
            const pfMemberId = String(formData.pfMemberId || "").trim();
            if (!/^[A-Za-z0-9]{22}$/.test(pfMemberId)) {
                errors.pfMemberId = 'PF Member ID must be exactly 22 alphanumeric characters.';
            }
        }
        
        // Check ESI fields only if ESI is applicable for the employee
        if (employeeData?.esiApplicable) {
            if (formData.hasESIIP === null) {
                errors.hasESIIP = 'Please select whether you hold an ESI IP number.';
            } else {
                // ESI State, District, and Dispensary are ALWAYS mandatory regardless of ESI IP answer
                if (!formData.esiState) {
                    errors.esiState = 'ESI State is required.';
                }
                
                if (!formData.esiDistrict) {
                    errors.esiDistrict = 'ESI District is required.';
                }
                
                if (!formData.esiDispensary) {
                    errors.esiDispensary = 'ESI Dispensary is required.';
                } else if (!/^[A-Za-z\s]+$/.test(formData.esiDispensary)) {
                    errors.esiDispensary = 'ESI Dispensary must contain only alphabets and spaces.';
                }
                
                // ESI IP Number is only mandatory when answer is "Yes"
                if (formData.hasESIIP) {
                    if (!formData.esiIPNumber) {
                        errors.esiIPNumber = 'ESI IP Number is required.';
                    } else if (formData.esiIPNumber.length !== 10) {
                        errors.esiIPNumber = 'ESI IP Number must be exactly 10 digits.';
                    } else if (!/^\d{10}$/.test(formData.esiIPNumber)) {
                        errors.esiIPNumber = 'ESI IP Number must contain only digits.';
                    }
                }
            }
        }
        
        // Set the errors state and return a boolean indicating form validity.
        setFormErrors(errors);
        // If only ESI is applicable, skip UAN validation check
        if (isOnlyEsiApplicable) {
            return Object.keys(errors).length === 0;
        }
        return Object.keys(errors).length === 0 && formData.hasUAN !== null;
    };

    // A single handler for both "Save" and "Next" actions to avoid code duplication.
    const handleSaveOrNext = async (action) => {
        // Validate the form before proceeding.
        const isFormValid = validateForm();
        if (!isFormValid) {
            // The form is invalid, so no action is taken.
            toast.error('Please fix the validation errors before saving.', { position: 'top-right' });
            return { saved: false };
        }

        try {
            // Update the parent component's state with the latest form data.
            // Note: `epsApplicable` is now set based on `epsEligiblePreviousCompany`.
            const epsApplicable = formData.epsEligiblePreviousCompany === true;
            setEmployeeData(prev => ({
                ...prev,
                ...formData,
                epsApplicable: epsApplicable ? true : prev.epsApplicable // Only set to true if user selects "Yes"
            }));

            // Call the save function from props. Include epsApplicable so backend persists it.
            await onSaveSection({ ...formData, epsApplicable });

            // If the action is 'next', call the next function from props.
            if (action === 'next' && typeof onNext === 'function' && !isLastTab) {
                onNext();
            }

            toast.success('Statutory details saved successfully', { position: 'top-right' });
            return { saved: true };
        } catch (error) {
            console.error('Error saving statutory details:', error);
            const msg =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to save. Please try again.';
            toast.error(msg, { position: 'top-right' });
            return { saved: false };
        }
    };

    // Keep ref updated so footer Update always uses current formData
    saveHandlerRef.current = () => handleSaveOrNext(isLastTab ? 'save' : 'next');

    useEffect(() => {
        if (typeof onSaveTrigger === 'function') {
            onSaveTrigger(() => saveHandlerRef.current?.());
        }
    }, [onSaveTrigger, isLastTab]);

    // This function will open the video link in a new tab
    const showVideoModal = () => {
        window.open('https://www.youtube.com/watch?v=vw3k-w_3g1I', '_blank');
    };
    
    // Handle EPS selection confirmation
    const handleEPSConfirmation = () => {
        if (pendingEPSSelection !== null) {
            setFormData(prev => ({
                ...prev,
                epsEligiblePreviousCompany: pendingEPSSelection
            }));
            setPendingEPSSelection(null);
        }
        setShowEPSModal(false);
    };
    
    // Handle EPS modal cancellation
    const handleEPSCancellation = () => {
        setPendingEPSSelection(null);
        setShowEPSModal(false);
    };

    return (
        <div className={`space-y-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <fieldset disabled={readOnly} className={readOnly ? 'opacity-75' : ''}>
            {/* Debug Info */}
            {/* <div className="bg-gray-100 p-2 text-xs">
                <strong>Debug:</strong> UAN: {String(formData.hasUAN)}, EPS: {String(formData.epsEligiblePreviousCompany)}, ESI: {String(formData.hasESIIP)}
            </div> */}

            {/* PF Section */}
            {!isOnlyEsiApplicable && (
            <>
            <div className="mb-4">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    PF Section
                </h3>
                <div className={`mt-2 border-t ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}></div>
            </div>

            {/* UAN Number Section - Hide if only ESI is applicable */}
            <div className={`space-y-4 ${!isPfApplicable ? 'opacity-50 cursor-not-allowed' : ''}`}>
    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                    <label className="text-sm font-medium text-gray-700">
                        Do you have UAN number? <span className="text-red-500">*</span>
                    </label>
                    <div className="flex space-x-4">
                        <label className="flex items-center">
                            <input
                                type="radio"
                                name="hasUAN"
                                value="true"
                                checked={formData.hasUAN === true}
                                onChange={handleInputChange}
                                disabled={!isPfApplicable}
                                className="mr-2 text-redhalo-navy focus:ring-redhalo-red/35"
                            />
                            Yes
                        </label>
                        <label className="flex items-center">
                            <input
                                type="radio"
                                name="hasUAN"
                                value="false"
                                checked={formData.hasUAN === false}
                                onChange={handleInputChange}
                                disabled={!isPfApplicable}
                                className="mr-2 text-redhalo-navy focus:ring-redhalo-red/35"
                            />
                            No
                        </label>
                    </div>
                </div>

                <div className="flex gap-3 flex-wrap ml-0 sm:ml-4">
                    <div className="w-56">
                    <FloatingInput
                        id="pfUANNumber"
                        name="pfUANNumber"
                        label="PF UAN Number"
                        value={formData.pfUANNumber}
                        onChange={(e) => {
                            const value = e.target.value;
                            handleInputChange(e);
                            
                            // Auto-validate UAN number as user types
                            if (value && value.length > 0) {
                                let error = '';
                                
                                // Check if it's exactly 12 digits
                                if (!/^\d{12}$/.test(value)) {
                                    error = 'UAN number must be exactly 12 digits';
                                }
                                // Check for invalid patterns
                                else if (value === '000000000000' || value === '111111111111' || value === '999999999999') {
                                    error = 'Please enter a valid UAN number';
                                }
                                // Check if it's all same digits
                                else if (/^(\d)\1{11}$/.test(value)) {
                                    error = 'UAN number cannot be all same digits';
                                }
                                
                                // Update validation errors
                                setFormErrors(prev => ({
                                    ...prev,
                                    pfUANNumber: error
                                }));
                            } else {
                                // Clear error if field is empty
                                setFormErrors(prev => ({
                                    ...prev,
                                    pfUANNumber: ''
                                }));
                            }
                        }}
                        error={formErrors.pfUANNumber}
                        placeholder="12 digits"
                        maxLength={12}
                        required
                        disabled={!isPfApplicable || formData.hasUAN !== true}

                    />
                    </div>
                    <div className="w-72">
                      <FloatingInput
                        id="pfMemberId"
                        name="pfMemberId"
                        label="PF Member ID"
                        value={formData.pfMemberId}
                        onChange={(e) => {
                          const value = String(e.target.value || "").replace(/[^A-Za-z0-9]/g, "").slice(0, 22);
                          setFormData((prev) => ({ ...prev, pfMemberId: value }));
                          if (!value) {
                            setFormErrors((prev) => ({ ...prev, pfMemberId: "" }));
                          } else if (!/^[A-Za-z0-9]{22}$/.test(value)) {
                            setFormErrors((prev) => ({
                              ...prev,
                              pfMemberId: "PF Member ID must be exactly 22 alphanumeric characters.",
                            }));
                          } else {
                            setFormErrors((prev) => ({ ...prev, pfMemberId: "" }));
                          }
                        }}
                        error={formErrors.pfMemberId}
                        placeholder="22 characters"
                        maxLength={22}
                        disabled={!isPfApplicable || formData.hasUAN !== true || isEmployeeView}
                      />
                    </div>
                </div>
                {formErrors.hasUAN && (
                    <div className="text-red-600 text-sm">{formErrors.hasUAN}</div>
                )}
                {!isPfApplicable && (
    <div className="text-redhalo-navy text-sm mt-1">
        This section is disabled because "PF Applicable" is set to 'No' in the Org Details tab.
    </div>
)}
                {formData.hasUAN === false && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start">
                            <AlertCircle className="text-red-500 mt-0.5 mr-3 flex-shrink-0" size={20} />
                            <div>
                                <p className="text-sm text-red-800">
                                    <strong>UAN number is mandatory to proceed.</strong> You must have a UAN number to continue. 
                                    If you don't have one, please generate your UAN number using UMAANG app and{' '}
                                    <button
                                        onClick={showVideoModal}
                                        className="text-redhalo-navy hover:text-redhalo-navy-dark underline inline-flex items-center"
                                    >
                                        click here
                                        <ExternalLink className="ml-1" size={16} />
                                    </button>{' '}
                                    to watch a video on the procedure. Then select "Yes" above and add the newly generated UAN number.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* EPS Eligibility Section - Hide if only ESI is applicable */}
            <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                    <label className="text-sm font-medium text-gray-700">
                        Are you eligible for EPS deduction in your Previous Company? <span className="text-red-500">*</span>
                    </label>
                    <div className="relative flex items-center space-x-2">
                        <Info
                            className="text-redhalo-red cursor-help hover:text-redhalo-navy"
                            size={16}
                            onMouseEnter={() => setShowEPSTooltip(true)}
                            onMouseLeave={() => setShowEPSTooltip(false)}
                        />
                        {showEPSTooltip && (
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg whitespace-nowrap z-10">
                                Check passbook for EPS deduction before confirming this
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                        )}
                        <div className="flex space-x-4">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="epsEligiblePreviousCompany"
                                    value="true"
                                    checked={formData.epsEligiblePreviousCompany === true}
                                    onChange={handleInputChange}
                                    className="mr-2 text-redhalo-navy focus:ring-redhalo-red/35"
                                />
                                Yes
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="epsEligiblePreviousCompany"
                                    value="false"
                                    checked={formData.epsEligiblePreviousCompany === false}
                                    onChange={handleInputChange}
                                    className="mr-2 text-redhalo-navy focus:ring-redhalo-red/35"
                                />
                                No
                            </label>
                        </div>
                    </div>
                </div>

                {/* {(
                    <div className="ml-0 sm: rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                        <div className="flex items-start">
                            <Info className="mt-0.5 mr-3 flex-shrink-0 text-emerald-600" size={20} />
                            <div>
                                <p className="text-sm text-emerald-900">
                                    EPS applicable is automatically checked. Please check your passbook for EPS deduction before confirming this.
                                </p>
                            </div>
                        </div>
                    </div>
                )} */}
                {formErrors.epsEligiblePreviousCompany && (
                    <div className="text-red-600 text-sm">{formErrors.epsEligiblePreviousCompany}</div>
                )}
            </div>
            </>
            )}

            {/* Divider between PF and ESI sections */}
            {!isOnlyEsiApplicable && isEsiApplicable && (
                <div className={`my-6 border-t ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}></div>
            )}

            {/* ESI Section - Only show when ESI is applicable and saved/updated in org details */}
            {/* isEsiApplicable checks employeeData?.esiApplicable === true, which means ESI was checked and saved in org details */}
            {isEsiApplicable && (
            <>
            <div className="mb-4">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    ESI Section
                </h3>
                <div className={`mt-2 border-t ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}></div>
            </div>
                <div className="space-y-2">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                        <label className="text-sm font-medium text-gray-700">
                            Do you hold an ESI IP number? <span className="text-red-500">*</span>
                        </label>
                        <div className="flex space-x-4">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="hasESIIP"
                                    value="true"
                                    checked={formData.hasESIIP === true}
                                    onChange={handleInputChange}
                                    className="mr-2 text-redhalo-navy focus:ring-redhalo-red/35"
                                />
                                Yes
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="hasESIIP"
                                    value="false"
                                    checked={formData.hasESIIP === false}
                                    onChange={handleInputChange}
                                    className="mr-2 text-redhalo-navy focus:ring-redhalo-red/35"
                                />
                                No
                            </label>
                        </div>
                        <div className="flex items-center space-x-4">
                            {/* ESI IP Number - Only show when answer is "Yes" */}
                            {formData.hasESIIP && (
                                <div className="w-48">
                                    <FloatingInput
                                        id="esiIPNumber"
                                        name="esiIPNumber"
                                        label="ESI IP Number"
                                        value={formData.esiIPNumber}
                                        onChange={handleInputChange}
                                        error={formErrors.esiIPNumber}
                                        placeholder="10 digits"
                                        maxLength={10}
                                        required
                                    />
                                </div>
                            )}
                            
                            {/* ESI State - Always visible */}
                            <div className="w-48">
                                <SearchableSelect
                                    label="ESI State"
                                    staticOptions={states.map(state => ({ value: state, label: state }))}
                                    value={formData.esiState}
                                    onChange={(e) => {
                                        const selectedState = e.target.value;
                                        setFormData(prev => ({ ...prev, esiState: selectedState, esiDistrict: '' }));
                                    }}
                                    required
                                    error={formErrors.esiState}
                                />
                            </div>
                            
                            {/* ESI District - Always visible */}
                            <div className="w-48">
                                <SearchableSelect
                                    label="ESI District"
                                    staticOptions={formData.esiState ? districts[formData.esiState]?.map(district => ({ value: district, label: district })) || [] : []}
                                    value={formData.esiDistrict}
                                    onChange={(e) => {
                                        setFormData(prev => ({ ...prev, esiDistrict: e.target.value }));
                                    }}
                                    required
                                    error={formData.esiState && formErrors.esiDistrict}
                                    disabled={!formData.esiState}
                                />
                            </div>
                            
                            {/* ESI Dispensary - Always visible */}
                            <div className="w-48">
                                <FloatingInput
                                    id="esiDispensary"
                                    name="esiDispensary"
                                    label="ESI Dispensary"
                                    value={formData.esiDispensary}
                                    onChange={handleInputChange}
                                    error={formErrors.esiDispensary}
                                    placeholder="Town/city name"
                                    required
                                />
                            </div>
                        </div>
                    </div>
                    {formErrors.hasESIIP && (
                        <div className="text-red-600 text-sm">{formErrors.hasESIIP}</div>
                    )}
                    {/* {formErrors.esiIPNumber && (
                        <div className="text-red-600 text-sm">{formErrors.esiIPNumber}</div>
                    )}
                    {formErrors.esiDispensary && (
                        <div className="text-red-600 text-sm">{formErrors.esiDispensary}</div>
                    )} */}
                </div>
            </>
            )}

            </fieldset>

            {/* Feedback Messages */}
            {feedback && (
                <div className={`mt-4 p-3 rounded-md ${
                    feedback.type === 'success'
                        ? 'border border-emerald-200 bg-emerald-50 text-emerald-900'
                        : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                     {typeof feedback.message === 'string'
            ? feedback.message
            : JSON.stringify(feedback.message, null, 2)}
                </div>
            )}
            
            {/* EPS Confirmation Modal */}
            {showEPSModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
                        <div className="flex items-center mb-4">
                            <Info className="text-redhalo-red mr-3" size={24} />
                            <h3 className="text-lg font-semibold text-gray-900">
                                EPS Deduction Confirmation
                            </h3>
                        </div>
                        
                        <div className="mb-6">
                            <p className="text-gray-700">
                                Please check your PF passbook for EPS deduction before confirming this.
                            </p>
                        </div>
                        
                        <div className="flex justify-end space-x-3">
                            {/* <button
                                onClick={handleEPSCancellation}
                                className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                Cancel
                            </button> */}
                            <button
                                onClick={handleEPSConfirmation}
                                className="px-4 py-2 bg-redhalo-mint/400 text-white rounded-md hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-redhalo-red/35"
                            >
                              Yes, I Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}